import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Button,
  CircularProgress,
  useTheme,
  Stack,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined,
  TrendingUpOutlined,
  TuneOutlined,
  SecurityOutlined,
  RefreshOutlined,
  WarningAmberOutlined,
  CheckCircleOutline,
  SearchOutlined,
  PaymentsOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import RevenueReport from "./RevenueReport";
import FeeConfigManagement from "./FeeConfigManagement";
import WalletAudit from "./WalletAudit";
import WithdrawalApprovals from "./WithdrawalApprovals";
import { formatKES } from "../WalletManagement/WalletBalance";
import { companyManagementUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import {
  WALLET_QUERY,
  WALLET_LEDGER_QUERY,
  SABABISHA_SHORT_CODE_QUERY,
  createWalletClient,
} from "../../data/WalletService/walletGraphQL";

const SababishaAdmin = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [activeTab, setActiveTab] = useState(0);
  const [wallets, setWallets] = useState([]);
  const [loadingWallets, setLoadingWallets] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);
  const [sharedShortCode, setSharedShortCode] = useState("9617815");
  // Per-company ledger totals, loaded lazily for visible rows only
  const [ledgerTotals, setLedgerTotals] = useState({});
  const ledgerTotalsRef = React.useRef({}); // companyId -> totals | "loading"

  const fetchAllWallets = useCallback(async () => {
    setLoadingWallets(true);
    try {
      // 1. Fetch active company list using the canonical MCB Switch pattern
      const compRes = await fetchJsonDynamic("companies", null, null, companyManagementUrl.uri);
      let rawCompanies = [];
      if (compRes?.results) {
        try {
          rawCompanies = JSON.parse(compRes.results) || [];
        } catch (e) {
          console.error("Error parsing company data:", e);
        }
      } else if (Array.isArray(compRes)) {
        rawCompanies = compRes;
      }

      // Filter active companies and sort by most recent (highest Id first)
      const companies = Array.isArray(rawCompanies)
        ? rawCompanies
          .filter((c) => c?.IsActive || c?.isActive)
          .sort((a, b) => (b?.Id || b?.id || 0) - (a?.Id || a?.id || 0))
        : [];

      if (!companies.length) {
        setWallets([]);
        return;
      }

      // 2. Fan out WALLET_QUERY and WALLET_LEDGER_QUERY per active company
      const walletClient = createWalletClient();
      // Wallet balances only, in chunks. Ledger totals are fetched lazily for
      // the rows currently visible (see the ledger-totals effect below).
      const chunkSize = 5;
      const walletResults = [];
      for (let i = 0; i < companies.length; i += chunkSize) {
        const chunk = companies.slice(i, i + chunkSize);
        const wChunk = await Promise.allSettled(
          chunk.map((c) => {
            const cid = c.Id ?? c.id;
            return walletClient.query({ query: WALLET_QUERY, variables: { companyId: cid } });
          })
        );
        walletResults.push(...wChunk);
      }
      ledgerTotalsRef.current = {};
      setLedgerTotals({});

      const merged = companies.map((c, i) => {
        const cid = c.Id ?? c.id;
        const name = c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? `Tenant #${String(cid).padStart(4, "0")}`;
        const w = walletResults[i].status === "fulfilled" ? walletResults[i].value?.data?.wallet : null;

        return {
          companyId: cid,
          companyName: name,
          availableBalance: w?.availableBalance ?? 0,
          escrowedBalance: w?.escrowedBalance ?? 0,
          minimumRetainedBalance: w?.minimumRetainedBalance ?? 0,
          lifecycleState: w?.lifecycleState ?? null,
          isBelowThreshold:
            w != null &&
            w.minimumRetainedBalance != null &&
            w.availableBalance < w.minimumRetainedBalance,
        };
      });

      setWallets(merged);
    } catch (err) {
      console.warn("Failed to fetch all company wallets:", err.message);
      setWallets([]);
    } finally {
      setLoadingWallets(false);
    }
  }, []);

  useEffect(() => {
    fetchAllWallets();
  }, [fetchAllWallets]);

  // Fetch the Sababisha Global Paybill shortcode from dbo.mpesa_credentials
  useEffect(() => {
    const walletClient = createWalletClient();
    walletClient
      .query({ query: SABABISHA_SHORT_CODE_QUERY })
      .then((res) => {
        const sc = res?.data?.sababishaShortCode;
        if (sc) setSharedShortCode(sc);
      })
      .catch((err) => {
        console.warn("Could not fetch Sababisha shortcode:", err.message);
      });
  }, []);

  const totalHeldFunds = wallets.reduce((sum, w) => sum + (w.availableBalance || 0), 0);
  const totalEscrow = wallets.reduce((sum, w) => sum + (w.escrowedBalance || 0), 0);

  // "Active" is judged on balances only; ledger totals are loaded lazily
  // per visible row, so they can't be used to filter the full list.
  const isActiveWallet = (w) =>
    (w.availableBalance || 0) > 0 || (w.escrowedBalance || 0) > 0;

  const activeWalletsCount = wallets.filter(isActiveWallet).length;

  const filteredWallets = wallets.filter((w) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const name = (w.companyName || "").toLowerCase();
      const idStr = String(w.companyId || "");
      return name.includes(q) || idStr.includes(q);
    }
    if (activeOnly) return isActiveWallet(w);
    return true;
  });

  const pageWallets = filteredWallets.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const paginatedWallets = pageWallets.map((w) => ({
    ...w,
    totalDeposited: ledgerTotals[w.companyId]?.totalDeposited ?? null,
    totalDisbursed: ledgerTotals[w.companyId]?.totalDisbursed ?? null,
  }));

  // Load ledger totals only for the rows on screen, once per company.
  const visibleIdsKey = pageWallets.map((w) => w.companyId).join(",");
  useEffect(() => {
    if (activeTab !== 0 || !visibleIdsKey) return;
    let cancelled = false;
    (async () => {
      const walletClient = createWalletClient();
      for (const idStr of visibleIdsKey.split(",")) {
        const cid = Number(idStr);
        if (ledgerTotalsRef.current[cid]) continue; // loaded or in flight
        ledgerTotalsRef.current[cid] = "loading";
        try {
          const res = await walletClient.query({
            query: WALLET_LEDGER_QUERY,
            variables: { companyId: cid, page: 1, pageSize: 200 },
          });
          let totalDeposited = 0;
          let totalDisbursed = 0;
          (res?.data?.walletLedger || []).forEach((e) => {
            const amt = Math.abs(Number(e.amount || 0));
            const tx = (e.txType || "").toUpperCase();
            if (tx.includes("TOPUP") || tx.includes("TOP_UP")) {
              totalDeposited += amt;
            } else if (
              tx.includes("DISBURSEMENT") ||
              tx.includes("ESCROW_COMMIT") ||
              (tx.includes("WITHDRAWAL") && !tx.includes("ROYALTY"))
            ) {
              totalDisbursed += amt;
            }
          });
          ledgerTotalsRef.current[cid] = { totalDeposited, totalDisbursed };
          if (!cancelled) {
            setLedgerTotals((prev) => ({
              ...prev,
              [cid]: { totalDeposited, totalDisbursed },
            }));
          }
        } catch {
          delete ledgerTotalsRef.current[cid];
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visibleIdsKey, activeTab, wallets]);

  return (
    <Box m={{ xs: "12px", md: "24px" }} sx={{ minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Breadcrumb */}
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.85rem" }}>
          <Link underline="hover" color="inherit" href="/dashboard">
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 600 }}>
            Sababisha Super Admin
          </Typography>
          <Typography color="text.primary">Global Paybill Network</Typography>
        </Breadcrumbs>
      </Box>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: colors.grey[100], fontFamily: "Outfit, Inter, sans-serif" }}
          >
            Global Paybill Network Administration
          </Typography>
          <Chip
            label="Super Admin Portal"
            color="primary"
            size="small"
            sx={{ fontWeight: 700 }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.5 }}>
          Centralized treasury oversight, fee structure management, and cross-company escrow reconciliation.
        </Typography>
      </Box>

      {/* Aggregate KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                TOTAL INSTITUTIONAL POOL (AVAILABLE)
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#1766A2", mt: 0.5 }}>
                {formatKES(totalHeldFunds)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Across {activeWalletsCount} active {activeWalletsCount === 1 ? "tenant" : "tenants"} ({wallets.length} registered)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                ACTIVE IN-FLIGHT ESCROW
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#ed6c02", mt: 0.5 }}>
                {formatKES(totalEscrow)}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Funds currently locked awaiting B2C callbacks
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                SHARED DARAJA SHORTCODE
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: "#22bf72", mt: 0.5 }}>
                {sharedShortCode}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                B2C / C2B / STK Shared Rail
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, val) => setActiveTab(val)}
        variant="fullWidth"
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
        }}
      >
        <Tab icon={<AccountBalanceWalletOutlined />} iconPosition="start" label="Company Wallets" />
        <Tab icon={<TrendingUpOutlined />} iconPosition="start" label="Revenue Performance" />
        <Tab icon={<TuneOutlined />} iconPosition="start" label="Fee Configuration" />
        <Tab icon={<SecurityOutlined />} iconPosition="start" label="Audit & Reconciliation" />
        <Tab icon={<PaymentsOutlined />} iconPosition="start" label="Withdrawal Approvals" />
      </Tabs>

      {/* Tab 0: Company Wallets Overview */}
      {activeTab === 0 && (
        <Card
          sx={{
            borderRadius: 2.5,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                mb: 2.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                  All MFI Institution Company Wallets
                </Typography>
                <Chip
                  label={`${filteredWallets.length} Tenants`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 700, fontSize: "0.75rem", height: 24 }}
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={activeOnly}
                      onChange={(e) => {
                        setActiveOnly(e.target.checked);
                        setPage(0);
                      }}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: "0.82rem", fontWeight: 600, color: colors.grey[200] }}>
                      Active Only
                    </Typography>
                  }
                  sx={{ mr: 0.5 }}
                />
                <TextField
                  placeholder="Search company or tenant #..."
                  size="small"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined sx={{ fontSize: 18, color: colors.grey[400] }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 260,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                    },
                  }}
                />
                <Tooltip title="Refresh wallet data from database">
                  <IconButton
                    size="small"
                    onClick={fetchAllWallets}
                    disabled={loadingWallets}
                  >
                    {loadingWallets ? (
                      <CircularProgress size={18} />
                    ) : (
                      <RefreshOutlined sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {loadingWallets ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : filteredWallets.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                  {searchQuery
                    ? `No company wallets match "${searchQuery}".`
                    : "No company wallets found. Opt-in companies will appear here once they activate the Sababisha Global Paybill."}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Available Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Escrowed Balance</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Deposited</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Disbursed</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedWallets.map((row) => (
                        <TableRow key={row.companyId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.companyName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                              Tenant #{String(row.companyId).padStart(4, "0")}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatKES(row.availableBalance)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "#ed6c02" }}>
                            {formatKES(row.escrowedBalance)}
                          </TableCell>
                          <TableCell align="right">
                            {row.totalDeposited == null ? "…" : formatKES(row.totalDeposited)}
                          </TableCell>
                          <TableCell align="right">
                            {row.totalDisbursed == null ? "…" : formatKES(row.totalDisbursed)}
                          </TableCell>
                          <TableCell>
                            {row.isBelowThreshold ? (
                              <Chip
                                icon={<WarningAmberOutlined fontSize="small" />}
                                label="Low Balance"
                                color="warning"
                                size="small"
                              />
                            ) : (
                              <Chip
                                icon={<CheckCircleOutline fontSize="small" />}
                                label="Healthy"
                                color="success"
                                size="small"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredWallets.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  sx={{
                    borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"
                      }`,
                  }}
                />
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 1: Revenue Reports */}
      {activeTab === 1 && <RevenueReport />}

      {/* Tab 2: Fee Configuration */}
      {activeTab === 2 && <FeeConfigManagement />}

      {/* Tab 3: System Audit Ledger */}
      {activeTab === 3 && <WalletAudit />}

      {/* Tab 4: Withdrawal Approvals */}
      {activeTab === 4 && <WithdrawalApprovals />}
    </Box>
  );
};

export default SababishaAdmin;
